import React, { Fragment, useState, useCallback, MouseEvent } from "react";
import { Input, Box } from "@thenounproject/lingo-core";
import ModalHeader from "../../../../app/_components/ModalHeader";
import ModalFooter from "../../../../app/_components/ModalFooter";
import ModalBody from "../../../../app/_components/ModalBody";
import useCreateMarket from "@redux/actions/markets/useCreateMarket";
import { useSelectSpace } from "@selectors/entities/spaces";
import useUpdateMarket from "@actions/markets/useUpdateMarket";

type Props = {
  dismissModal?: (e?: MouseEvent | KeyboardEvent) => void;
  initialName?: string;
  isEdit?: boolean;
  marketId?: string;
  onSuccess?: (uuid: string) => void;
};

const AddEditMarketModal: React.FC<Props> = ({
  dismissModal,
  initialName = "",
  isEdit = false,
  marketId,
  onSuccess,
}) => {
  const [name, setName] = useState(initialName);
  const space = useSelectSpace();
  const [createMarket, { isProcessing }] = useCreateMarket();
  const [updateMarket, { isProcessing: isUpdating }] = useUpdateMarket();

  const handleNameChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!name.trim()) return;

    if (isEdit) {
      const { error } = await updateMarket({
        spaceId: space.id,
        id: marketId,
        name: name.trim(),
      });
      if (!error && dismissModal) {
        dismissModal();
      }
      return;
    }

    const { error, response } = await createMarket({
      spaceId: space.id,
      name: name.trim(),
      displayOrder: "append",
    });

    if (!error) {
      if (response) {
        const newMarket = response.entities?.markets?.[response.result];
        if (newMarket) onSuccess?.(newMarket.uuid);
      }
      dismissModal?.();
    }
  }, [name, isEdit, space.id, createMarket, dismissModal, marketId, updateMarket, onSuccess]);

  return (
    <Fragment>
      <ModalHeader title={isEdit ? "Edit Market" : "Add Market"} />
      <ModalBody>
        <Box>
          <Input
            label="Name"
            value={name}
            onChange={handleNameChange}
            placeholder="Enter market name"
            autoFocus
            onSubmit={() => void handleSubmit()}
          />
        </Box>
      </ModalBody>
      <ModalFooter
        primary={{
          text: isProcessing || isUpdating ? "Saving..." : "Save Market",
          onClick: handleSubmit,
          disabled: !name.trim() || isProcessing || isUpdating,
        }}
      />
    </Fragment>
  );
};

export default AddEditMarketModal;
